import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api.js';

/**
 * Every page uses React Query. In the Base44 build only the Purchase Orders
 * page did, and even there three of its six query keys were never invalidated —
 * so the low-stock banner and the product picker went stale the moment anything
 * was received or allocated.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry(failureCount, error) {
        // Retrying a 4xx just repeats the same rejection.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** Query keys, in one place so invalidation cannot miss one. */
export const keys = {
  me: ['me'],
  dashboard: ['dashboard'],
  products: (params) => ['products', params ?? {}],
  product: (id) => ['product', id],
  vendors: (params) => ['vendors', params ?? {}],
  vendorOrders: (id) => ['vendor-orders', id],
  purchaseOrders: (params) => ['purchase-orders', params ?? {}],
  purchaseOrder: (id) => ['purchase-order', id],
  poCounts: ['po-counts'],
  shippingLocations: ['shipping-locations'],
  companySettings: ['company-settings'],
  emailSettings: ['email-settings'],
  emailTemplates: (params) => ['email-templates', params ?? {}],
  templateVariables: ['template-variables'],
  users: (params) => ['users', params ?? {}],
  activity: (params) => ['activity', params ?? {}],
  allocations: (params) => ['allocations', params ?? {}],
  reorderNeeds: ['reorder-needs'],
};

/**
 * Anything that changes stock or order state touches several views at once.
 * Callers invalidate a group rather than trying to remember the list.
 */
export function invalidateInventory(client = queryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['products'] }),
    client.invalidateQueries({ queryKey: ['product'] }),
    client.invalidateQueries({ queryKey: ['purchase-orders'] }),
    client.invalidateQueries({ queryKey: ['purchase-order'] }),
    client.invalidateQueries({ queryKey: keys.poCounts }),
    client.invalidateQueries({ queryKey: keys.dashboard }),
    client.invalidateQueries({ queryKey: ['activity'] }),
    client.invalidateQueries({ queryKey: ['reorder-needs'] }),
    // Recent allocations on the Allocations page, and each vendor's product
    // count and order history on Vendors — all stale after stock moves.
    client.invalidateQueries({ queryKey: ['allocations'] }),
    client.invalidateQueries({ queryKey: ['vendors'] }),
    client.invalidateQueries({ queryKey: ['vendor-orders'] }),
  ]);
}
